import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  ArrowRightLeft,
  TrendingUp,
  TrendingDown,
  Loader2,
  AlertCircle,
  Info,
  Wallet,
  Coins,
  ArrowDown,
  RefreshCw
} from 'lucide-react';

import { getApiUrl, getAuthenticatedAxios } from '../../utils/api';

const API_URL = getApiUrl();

const BlumTradeForm = ({ selectedToken, watchedTokens = [], tonPrice, onSelectToken }) => {
  const [tradeType, setTradeType] = useState('buy');
  const [amount, setAmount] = useState('0.1'); // Min 0.1 TON for BLUM
  const [slippage, setSlippage] = useState('5'); // slippage info only, actual min_receive is 1
  const [estimating, setEstimating] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [loadingWallets, setLoadingWallets] = useState(true);
  const [availableTokens, setAvailableTokens] = useState([]);
  const [loadingTokens, setLoadingTokens] = useState(true);

  // Load wallets and tokens
  useEffect(() => {
    loadWallets();
    loadTokens();
  }, []);

  const loadTokens = async () => {
    try {
      const api = getAuthenticatedAxios();
      // Get discovered tokens (all available for trading)
      const response = await api.get(`${API_URL}/api/blum/discover/tokens`, {
        params: { limit: 100, include_watched: true }
      });
      
      if (response.data.success && response.data.tokens) {
        // Show all tokens - bonding curve tokens may have price = 0 initially
        setAvailableTokens(response.data.tokens);
      }
    } catch (error) {
      console.error('Error loading tokens:', error);
    } finally {
      setLoadingTokens(false);
    }
  };

  // Combine watched and available tokens
  const allTokens = [...new Map([...watchedTokens, ...availableTokens].map(t => [t.master_address, t])).values()];

  // Estimate when amount changes
  useEffect(() => {
    if (amount && parseFloat(amount) > 0 && selectedToken) {
      const timeout = setTimeout(() => {
        estimateTrade();
      }, 500);
      return () => clearTimeout(timeout);
    } else {
      setEstimate(null);
    }
  }, [amount, tradeType, selectedToken]);

  const loadWallets = async () => {
    setLoadingWallets(true);
    try {
      const api = getAuthenticatedAxios();
      // Add timestamp to bypass cache
      const response = await api.get(`${API_URL}/api/wallets/list?t=${Date.now()}`);
      
      if (response.data.wallets) {
        // Filter wallets with TON balance > 0.05 and sort by balance descending
        const activeWallets = response.data.wallets
          .filter(w => parseFloat(w.ton_balance || 0) / 1e9 > 0.05)
          .sort((a, b) => parseFloat(b.ton_balance || 0) - parseFloat(a.ton_balance || 0));
        
        setWallets(activeWallets);
        
        // Select wallet with highest balance
        if (activeWallets.length > 0 && !selectedWallet) {
          setSelectedWallet(activeWallets[0]);
        }
      }
    } catch (error) {
      console.error('Error loading wallets:', error);
      toast.error('Ошибка загрузки кошельков');
    } finally {
      setLoadingWallets(false);
    }
  };

  const estimateTrade = async () => {
    if (!selectedToken || !amount) return;

    setEstimating(true);
    try {
      const api = getAuthenticatedAxios();
      const endpoint = tradeType === 'buy' ? 'estimate/buy' : 'estimate/sell';
      
      const response = await api.post(`${API_URL}/api/blum/${endpoint}`, {
        token_address: selectedToken.master_address,
        amount: parseFloat(amount)
      });
      
      if (response.data.success) {
        setEstimate(response.data);
      } else {
        setEstimate(null);
      }
    } catch (error) {
      console.error('Estimate error:', error);
      setEstimate(null);
    } finally {
      setEstimating(false);
    }
  };

  const formatNumber = (num, decimals = 4) => {
    if (num === null || num === undefined) return '—';
    if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
    return num.toFixed(decimals);
  };

  const formatPrice = (price) => {
    if (!price) return '$0';
    if (price < 0.00001) return `$${price.toExponential(2)}`;
    if (price < 0.01) return `$${price.toFixed(6)}`;
    if (price < 1) return `$${price.toFixed(4)}`;
    return `$${price.toFixed(2)}`;
  };

  const [executing, setExecuting] = useState(false);

  const handleTrade = async () => {
    if (!selectedToken || !amount || !selectedWallet) {
      toast.error('Заполните все поля');
      return;
    }

    // Check if token exists (removed strict price check - bonding curve tokens start at 0)

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      toast.error('Введите корректную сумму');
      return;
    }

    // Min 0.1 TON for BLUM buy
    if (tradeType === 'buy' && amountNum < 0.1) {
      toast.error('Минимальная сумма покупки: 0.1 TON');
      return;
    }

    setExecuting(true);
    try {
      const api = getAuthenticatedAxios();
      const endpoint = tradeType === 'buy' ? '/api/blum/trade/buy' : '/api/blum/trade/sell';
      
      const response = await api.post(`${API_URL}${endpoint}`, {
        token_address: selectedToken.master_address,
        wallet_address: selectedWallet.address,
        amount: amountNum,
        slippage_percent: parseFloat(slippage)
      });

      if (response.data.success) {
        toast.success(
          tradeType === 'buy' 
            ? `Куплено ${selectedToken.symbol}!` 
            : `Продано ${selectedToken.symbol}!`
        );
        setAmount('');
      } else {
        toast.error(response.data.error || 'Ошибка выполнения сделки');
      }
    } catch (error) {
      console.error('Trade error:', error);
      toast.error(error.response?.data?.detail || error.message || 'Ошибка сети');
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Trade Form */}
      <Card className="bg-white/10 backdrop-blur-sm border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" />
            {tradeType === 'buy' ? 'Покупка токенов' : 'Продажа токенов'}
          </CardTitle>
          <CardDescription className="text-purple-200">
            {tradeType === 'buy' 
              ? 'Обмен TON на BLUM токены' 
              : 'Обмен BLUM токенов на TON'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Trade Type Toggle */}
          <div className="flex gap-2 p-1 bg-white/5 rounded-lg">
            <Button
              variant={tradeType === 'buy' ? 'default' : 'ghost'}
              className={`flex-1 ${tradeType === 'buy' 
                ? 'bg-green-500 hover:bg-green-600 text-white' 
                : 'text-purple-300 hover:text-white hover:bg-white/10'}`}
              onClick={() => setTradeType('buy')}
            >
              <TrendingUp className="h-4 w-4 mr-2" />
              Buy
            </Button>
            <Button
              variant={tradeType === 'sell' ? 'default' : 'ghost'}
              className={`flex-1 ${tradeType === 'sell' 
                ? 'bg-red-500 hover:bg-red-600 text-white' 
                : 'text-purple-300 hover:text-white hover:bg-white/10'}`}
              onClick={() => setTradeType('sell')}
            >
              <TrendingDown className="h-4 w-4 mr-2" />
              Sell
            </Button>
          </div>

          {/* Token Selection */}
          <div>
            <label className="text-purple-200 text-sm mb-2 block">Токен</label>
            {loadingTokens ? (
              <div className="h-10 bg-white/10 rounded flex items-center justify-center">
                <Loader2 className="h-4 w-4 animate-spin text-purple-400" />
              </div>
            ) : (
              <Select 
                value={selectedToken?.master_address || ''} 
                onValueChange={(addr) => {
                  const token = allTokens.find(t => t.master_address === addr);
                  onSelectToken(token);
                }}
              >
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Выберите токен" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-white/20 max-h-60">
                  {allTokens.length === 0 ? (
                    <div className="p-2 text-purple-300 text-sm text-center">Нет доступных токенов</div>
                  ) : (
                    allTokens.map((token) => {
                      const isActive = token.trading_enabled !== false && (token.last_price_ton > 0 || token.ton_liq_collected > 1);
                      return (
                        <SelectItem 
                          key={token.master_address} 
                          value={token.master_address}
                          className={`text-white hover:bg-white/10 ${!isActive ? 'opacity-50' : ''}`}
                        >
                          {!isActive && '⚠️ '}{token.symbol} - {token.name} ({(token.ton_liq_collected || 0).toFixed(1)} TON)
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Wallet Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-purple-200 text-sm flex items-center gap-2">
                <Wallet className="h-4 w-4" />
                Кошелек
              </label>
              <Button
                variant="ghost"
                size="sm"
                onClick={loadWallets}
                disabled={loadingWallets}
                className="h-6 px-2 text-purple-300 hover:text-white hover:bg-white/10"
              >
                <RefreshCw className={`h-3 w-3 ${loadingWallets ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            {loadingWallets ? (
              <div className="flex items-center gap-2 text-purple-300 py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Загрузка кошельков...
              </div>
            ) : wallets.length === 0 ? (
              <div className="flex items-center gap-2 text-yellow-400 py-2">
                <AlertCircle className="h-4 w-4" />
                Нет кошельков с балансом
              </div>
            ) : (
              <Select 
                value={selectedWallet?._id || selectedWallet?.address || ''} 
                onValueChange={(id) => {
                  const wallet = wallets.find(w => (w._id || w.address) === id);
                  setSelectedWallet(wallet);
                }}
              >
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Выберите кошелек" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-white/20 max-h-60">
                  {wallets.map((wallet) => (
                    <SelectItem 
                      key={wallet._id || wallet.address} 
                      value={wallet._id || wallet.address}
                      className="text-white hover:bg-white/10"
                    >
                      {wallet.group_name || 'Wallet'} #{wallet.wallet_index || 1} - {(parseFloat(wallet.ton_balance || 0) / 1e9).toFixed(2)} TON
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Amount Input */}
          <div>
            <label className="text-purple-200 text-sm mb-2 block flex items-center gap-2">
              <Coins className="h-4 w-4" />
              {tradeType === 'buy' ? 'Сумма TON' : 'Количество токенов'}
            </label>
            <div className="relative">
              <Input
                type="number"
                placeholder={tradeType === 'buy' ? '0.5' : '10000'}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-purple-300 pr-16"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-300">
                {tradeType === 'buy' ? 'TON' : selectedToken?.symbol || 'TOKEN'}
              </span>
            </div>
            
            {/* Quick amount buttons - min 0.1 TON for BLUM */}
            {tradeType === 'buy' && selectedWallet && (
              <div className="flex gap-2 mt-2">
                {[0.1, 0.2, 0.5, 1].map((val) => (
                  <Button
                    key={val}
                    size="sm"
                    variant="outline"
                    className="flex-1 bg-white/5 border-white/10 text-purple-200 hover:bg-white/10"
                    onClick={() => setAmount(val.toString())}
                  >
                    {val} TON
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* Slippage - informational only, actual trades use min_receive=1 */}
          <div>
            <label className="text-purple-200 text-sm mb-2 block">Slippage Tolerance</label>
            <div className="flex gap-2">
              {['1', '3', '5', '10'].map((val) => (
                <Button
                  key={val}
                  size="sm"
                  variant={slippage === val ? 'default' : 'outline'}
                  className={slippage === val 
                    ? 'bg-purple-500 text-white' 
                    : 'bg-white/5 border-white/10 text-purple-200 hover:bg-white/10'}
                  onClick={() => setSlippage(val)}
                >
                  {val}%
                </Button>
              ))}
            </div>
          </div>

          {/* Execute Button */}
          <Button 
            onClick={handleTrade}
            disabled={!selectedToken || !amount || !selectedWallet || executing}
            className={`w-full ${tradeType === 'buy' 
              ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600' 
              : 'bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600'} text-white`}
          >
            {executing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Выполняется...
              </>
            ) : tradeType === 'buy' ? (
              <>
                <TrendingUp className="h-4 w-4 mr-2" />
                Купить токены
              </>
            ) : (
              <>
                <TrendingDown className="h-4 w-4 mr-2" />
                Продать токены
              </>
            )}
          </Button>

          {/* Info */}
          <div className="flex items-start gap-2 text-purple-300 text-xs">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Торговля осуществляется через BLUM BCL контракты напрямую в блокчейне TON
            </span>
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

export default BlumTradeForm;


